/** Featured essay on Stories page — images: public/images/f1.webp … f9.jpeg */

const base = process.env.PUBLIC_URL || '';

export const meaningOfMourningMeta = {
  id: 'meaning-of-mourning',
  title: 'The Meaning of Mourning: Why Funerals Matter So Deeply in Akan Culture',
  subtitle: 'Honor, community, and the visual language of farewell',
};

export const meaningOfMourningBlocks = [
  {
    type: 'p',
    text:
      'In Akan society in Ghana, funerals are far more than occasions of sorrow. They are among the most important cultural gatherings in public life—moments when family, community, history, spirituality, and dignity all meet in one solemn, powerful space. To an outsider, an Akan funeral may seem lavish: royal umbrellas floating above chiefs and queen mothers, mourners wrapped in black and red Adinkra cloth, women adorned with beads, elders seated in stately composure, and a bereaved family dressed in deep mourning black. But beneath the beauty lies something far more profound. An Akan funeral is not a performance for its own sake. It is an act of honor.\n\nIt is where grief is made visible, where the dead are escorted into memory, and where the living prove that no one passes from this world without witness.',
  },
  {
    type: 'figure',
    src: `${base}/images/f9.webp`,
    alt: 'Akan traditional funeral scene',
    caption: 'Akan traditional funeral scene.',
  },
  {
    type: 'h2',
    text: 'More Than Ceremony: The Akan Funeral as Social Duty',
  },
  {
    type: 'p',
    text:
      'Among the Akan, funerals are one of the most respected communal obligations in life. Alongside marriage and major traditional celebrations, they stand as defining social events. A funeral is not simply arranged and attended; it is carried by a network of duty. The bereaved family hosts. Relatives travel from near and far. Friends, neighbors, colleagues, and community members arrive to offer condolences and support. They do not come empty-handed. Many bring bottles of drinks, sachet water, cash donations, and practical gifts to help the family shoulder the burden of mourning.\n\nAfter the burial, food and refreshments are served, often catered by the deceased’s immediate family. This hospitality is deeply meaningful. Even in grief, the family receives others. They acknowledge each visitor, feed those who have come to mourn, and welcome guests who may have traveled long distances. In this way, the funeral becomes not only a farewell to the dead, but a reaffirmation of the ties that bind the living.',
  },
  {
    type: 'figure',
    src: `${base}/images/f5.webp`,
    alt: 'Bereaved family and well wishers',
    caption: 'Bereaved family and well wishers.',
  },
  {
    type: 'h2',
    text: 'Why Funerals Matter So Much',
  },
  {
    type: 'p',
    text:
      'For the Akan, death does not erase belonging. A person remains part of the family and lineage even after death, and a proper funeral helps mark the transition from earthly life into honored remembrance. It is both spiritual and social, intimate and public.',
  },
  {
    type: 'list',
    items: [
      'Spiritual significance: The funeral helps ensure the deceased is properly honored and remembered within the moral and ancestral world of the family.',
      'Family responsibility: It tests whether relatives will unite, support one another, and preserve dignity in a time of loss.',
      'Public respect: The scale and care of a funeral often reflect the esteem in which the deceased and the family are held.',
      'Communal solidarity: Funerals draw people together across generations, towns, and even continents.',
      'Cultural preservation: Through dress, music, language, symbols, and protocol, Akan funerals preserve a living heritage.',
    ],
  },
  {
    type: 'p',
    text: 'In other words, a funeral is not simply about death. It is about belonging.',
  },
  {
    type: 'h2',
    text: 'The Language of Cloth, Color, and Presence',
  },
  {
    type: 'p',
    text:
      'One of the most striking dimensions of an Akan funeral is its visual richness. In Ghana, and especially among the Akan, cloth speaks. It conveys emotion, rank, relationship, and occasion.\n\nThe bereaved family may sit together in solid black, their appearance restrained, sorrowful, and composed. Their clothing often signals the full weight of loss. Around them, mourners appear in black and red cloths—colors associated with intense grief and mourning. In other contexts, black and white may be worn, especially when the funeral mood also carries remembrance, honor, and the celebration of a long life.',
  },
  {
    type: 'figure',
    src: `${base}/images/f6.webp`,
    alt: 'Figurative fantasy coffin',
    caption: 'Figurative fantasy coffin — Greater Accra.',
  },
  {
    type: 'p',
    text:
      'Adinkra cloth, with its symbolic motifs, adds another layer of meaning. These patterns are not merely decorative. They can suggest endurance, the brevity of life, the presence of God, wisdom, resilience, and the continuity of the soul. Beads, gold-toned jewelry, sandals, and finely wrapped cloth all contribute to a visual language of noble mourning. The textures—solid black, deep red, black and white contrasts, polished beadwork, soft woven surfaces—create an atmosphere that is at once somber and majestic.\n\nThis is why Akan funerals are often so visually unforgettable: grief is expressed not in chaos, but in form.',
  },
  {
    type: 'h2',
    text: 'Chiefs, Queen Mothers, and the Royal Umbrella',
  },
  {
    type: 'p',
    text:
      'When chiefs and queen mothers attend a funeral, their presence carries immense significance. They do not simply “grace the occasion”; they stand as visible representatives of communal respect and traditional authority. Their arrival may include attendants, ceremonial staffs, linguists, royal sandals, and elaborately decorated umbrellas held above them.\n\nThe umbrella in Akan political culture is not just an accessory. It is a symbol of status, shelter, and sacred presence. At a funeral, it signals that mourning has become a matter of communal recognition. The bereaved are not left alone in private pain. Authority itself has come to stand with them.',
  },
  {
    type: 'figure',
    src: `${base}/images/f7.webp`,
    alt: 'Figurative fantasy coffin',
    caption: 'Figurative fantasy coffin — Greater Accra.',
  },
  {
    type: 'p',
    text:
      'These details matter because hierarchy is an important part of Akan ceremonial life. Seating, greetings, posture, entourage, and attire all reflect social order. Yet this hierarchy does not diminish the humanity of the event. Rather, it frames it in dignity. The family’s grief is recognized publicly, respectfully, and with full cultural weight.',
  },
  {
    type: 'h2',
    text: 'Mourning Is Not Silence Alone',
  },
  {
    type: 'p',
    text:
      'To many non-Ghanaians, one of the most surprising aspects of Ghanaian funerals is that grief may be accompanied by drumming, singing, movement, and social gathering. But in Akan culture, sorrow does not have to be quiet to be sincere. Mourning can be spoken, sung, danced, wept, and wrapped in ritual. It can be solemn and social at once.\n\nThere are moments of profound sadness, but also moments of reunion, storytelling, cultural expression, and public memory. Relatives who have not seen one another in years may meet again at a funeral. Family history is retold. Old obligations are renewed. Social fractures may soften under the gravity of loss.',
  },
  {
    type: 'figure',
    src: `${base}/images/f8.webp`,
    alt: 'Figurative fantasy coffin',
    caption: 'Figurative fantasy coffin — Greater Accra.',
  },
  {
    type: 'p',
    text:
      'A funeral, then, becomes a stage not of vanity, but of truth: this was a life, these were its people, and this is how they came to honor it.',
  },
  {
    type: 'h2',
    text: 'The Grandeur—and the Misunderstanding',
  },
  {
    type: 'p',
    text:
      'It is true that some modern Ghanaian funerals can become highly elaborate, especially in urban settings, where wealth, status, and fashion sometimes shape the scale of display. Critics occasionally describe them as excessive or as social showpieces. There is some truth to the concern that funerals can become expensive and, at times, competitive. But that criticism must be handled carefully.\n\nTo dismiss the grandeur of Akan funerals as mere spectacle is to misunderstand their deeper purpose. Beauty, order, and public dignity are not superficial in this context. They are part of the moral duty of farewell. The cloth, the umbrellas, the processions, the chiefs, the beads, the food, and the ceremonial posture all say the same thing in different forms: this person’s life carried value, and the family has not failed them in death.',
  },
  {
    type: 'h2',
    text: 'Nana Yaa Asantewaa and the Memory of Women’s Power',
  },
  {
    type: 'p',
    text:
      'No serious reflection on Akan and especially Asante cultural identity is complete without remembering Nana Yaa Asantewaa, the Queen Mother of Ejisu and one of the most important women in West African history. In 1900, during the War of the Golden Stool, she rose in defiance of British colonial aggression and became the rallying force behind Asante resistance.\n\nYaa Asantewaa is remembered not only as a warrior, but as a woman of political courage, clarity, and authority. She challenged hesitation, defended the sacred dignity of Asante sovereignty, and stood for the principle that leadership is not the preserve of men alone. Though she was later exiled, her legacy remains enduring and revered.\n\nHer memory matters in any visual or cultural portrayal of Asante womanhood. When people invoke traditional Asante women as protectors of the kingdom and defenders of the king, Yaa Asantewaa stands as the most powerful historical reference. She represents disciplined strength, ancestral duty, and fearless guardianship. Her story reminds us that Akan funeral culture, like Akan history itself, is not only shaped by male authority. Women—especially queen mothers—have long held political, spiritual, and ceremonial power.',
  },
  {
    type: 'h2',
    text: 'Ghana’s Remarkable Figurative Coffins',
  },
  {
    type: 'p',
    text:
      'No discussion of funeral culture in Ghana is complete without mentioning the extraordinary figurative coffins made in the Greater Accra Region, especially among Ga communities. Often called “fantasy coffins,” these handcrafted caskets are designed in shapes that reflect the life, profession, dreams, or identity of the deceased.\n\nA fisherman may be buried in a coffin shaped like a canoe or fish. A farmer may be honored with a cocoa pod or ear of corn. A driver may have a car-shaped casket. A trader in beverages might be buried in a bottle-shaped casket. A devout Christian might be laid to rest in a Bible-shaped casket. These designs are both artistic and deeply personal.',
  },
  {
    type: 'figure',
    src: `${base}/images/f1.webp`,
    alt: 'Handcrafted figurative fantasy coffins unique to Ghanaians in the Greater Accra region',
    caption:
      'Handcrafted Figurative Fantasy Coffins unique only to Ghanaians in the greater Accra region. These are carved in accordance to one’s interest and career.',
  },
  {
    type: 'p',
    text:
      'To many non-Ghanaians, this practice is astonishing and fascinating. Yet it is rooted in an understandable idea: the final vessel of the dead should tell the story of who they were. Rather than erasing individuality, the coffin reflects it.',
  },
  {
    type: 'figure',
    src: `${base}/images/f2.webp`,
    alt: 'A Pilot and A Singer or Announcer',
    caption: 'A Pilot & A Singer or Announcer.',
  },
  {
    type: 'p',
    text:
      'The same workshop imagination can honor a life spent in the sky or behind a microphone—forms that announce, at a glance, how the community remembers the work and voice of the departed.',
  },
  {
    type: 'figure',
    src: `${base}/images/f3.webp`,
    alt: 'A Fisherman, Fishmonger and A Driver',
    caption: 'A Fisherman, Fishmonger & A Driver.',
  },
  {
    type: 'p',
    text:
      'Lives drawn from the sea, the market, or the road may each receive a vessel that speaks plainly to neighbors and strangers alike about what sustained the person who has gone.',
  },
  {
    type: 'figure',
    src: `${base}/images/f4.webp`,
    alt: 'Pastor and A Drummer',
    caption: 'Pastor & A Drummer.',
  },
  {
    type: 'p',
    text:
      'Spiritual calling and the pulse of the drum are not forgotten either: the coffin can carry Scripture, rhythm, and public faith as visibly as cloth carries grief.',
  },
  {
    type: 'p',
    text:
      'This tradition has gained international attention through renowned coffin makers from places such as Teshie, where the craft has become one of Ghana’s most distinctive artistic contributions to the world. It is important to note that while this practice is not specifically Akan in origin, it belongs to Ghana’s larger funerary imagination and reveals the country’s extraordinary ability to merge craftsmanship, symbolism, and memory in death rites.',
  },
  {
    type: 'h2',
    text: 'A Funeral as a Portrait of a People',
  },
  {
    type: 'p',
    text:
      'To witness an Akan funeral is to witness a culture speaking in symbols. The black cloth tells you grief has settled heavily. The red announces emotional pain and solemn mourning. The white suggests remembrance and peace. The umbrella declares authority and communal recognition. The beads and ornaments signal dignity. The food says those who mourn must also be cared for. The gathering of chiefs, queen mothers, relatives, neighbors, and friends says no one should leave this life unattended.\n\nAnd that may be the deepest lesson of all.\n\nIn Akan culture, a funeral is not simply about burying the dead. It is about affirming that every human life deserves honor, that grief belongs to the community as well as the family, and that memory must be carried with beauty as well as sorrow.\n\nThat is why funerals remain so important. They are not just ceremonies of loss. They are declarations of value.',
  },
];
